import {Component, forwardRef, Input} from '@angular/core';
import {AbstractControl, ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {errorMessages} from '../../core/errors/error-messages';
import * as _ from 'lodash';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgIf } from '@angular/common';
import { ExplanationComponent } from '../../core/visual/explanation/explanation.component';

@Component({
    selector: 'app-mat-checkbox-input',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MatCheckboxInputComponent),
            multi: true
        }
    ],
    template: `
    <div class="regular-form-field checkbox" [class.has-error]="showErrors()">
      <mat-checkbox [id]="_id" [name]="name" [checked]="checked" [disabled]="disabled || submitted"
                    (change)="onChange($event)">
        {{label}}
        <app-explanation *ngIf="explanation">
          <div [innerHTML]="explanation"></div>
        </app-explanation>
      </mat-checkbox>
      <span class="help-block" *ngIf="formControl?.invalid">
        {{firstError()}}
      </span>
      <span class="help-block info-block" *ngIf="infoText">{{infoText}}</span>
    </div>`,
    styles: [],
    standalone: true,
    imports: [MatCheckbox, NgIf, ExplanationComponent]
})
export class MatCheckboxInputComponent implements ControlValueAccessor {

  @Input() formControl: UntypedFormControl | AbstractControl;
  @Input() label: string;
  @Input() infoText: string;
  @Input() explanation: string;
  @Input() name: string;
  @Input() id: string;
  @Input() formValidators: any[];
  @Input() defValue;

  @Input()
  get submitted(): boolean { return this._submitted; }
  set submitted(value: boolean) {
    this._submitted = coerceBooleanProperty(value);
    if (this.formControl) {
      if (this._submitted) {
        this.formControl.disable();
      } else {
        this._disabled ? this.formControl.disable() : this.formControl.enable();
      }
    }
    // this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    if (this.formControl) {
      this._disabled ? this.formControl.disable() : this.formControl.enable();
    }
    // this.stateChanges.next();
  }

  _submitted: boolean;
  _disabled: boolean;
  checked: boolean;
  value: any;

  firstFormError: any;
  _id: string;

  propagateChange: Function;
  onTouched: Function;

  constructor() {
    this.setId();
  }

  setId(): void {
    if (this.id) {
      this._id = this.id;
    }
    if (this.name) {
      this._id = this._id = _.uniqueId(`${this.name}_`);
    }

    this._id = _.uniqueId('id_');
  }

  showErrors() {
    return (!this.isValid() && this.isDirty()) || (this.submitted && !this.isValid() || this.hasFormError());
  }

  isUnTouched() {
    return this.formControl && this.formControl.untouched;
  }

  isValid() {
    return this.formControl && this.formControl.valid;
  }

  isDirty() {
    return this.formControl && this.formControl.dirty;
  }

  hasFormError() {
    const formErrors = this.formControl?.parent?.errors || [];

    if (this.formValidators) {

      for (const error in formErrors) {
        if (formErrors.hasOwnProperty(error)) {
          this.firstFormError = this.formValidators.find((item) => {
            return item.key === error;
          });

          if (this.firstFormError) {
            return true;
          }
        }
      }
    }
    return false;
  }

  firstError(): string {

    if (this.hasFormError()) {
      return this.firstFormError.error;
    }

    if (this.formControl && this.formControl.errors) {
      for (const error in this.formControl.errors) {
        if (this.formControl.errors.hasOwnProperty(error)) {

          // get error description
          let message = errorMessages[error];
          if (!message) {
            message = `Unknown error: ${error}`;
          }
          return message;
        }
      }
    }

    return null;
  }

  errors(): string[] {
    const errorsMessages = [];
    if (this.formControl && this.formControl.errors) {
      for (const error in this.formControl.errors) {
        if (this.formControl.errors.hasOwnProperty(error)) {

          // get error description
          const message = errorMessages[error];
          if (message) {
            errorsMessages.push(message);
          } else {
            errorsMessages.push(`Unknown error: ${error}`);
          }
          console.log(error);
        }
      }
    }

    return errorsMessages;
  }

  writeValue(obj: any): void {
    this.value = obj;
    if (this.defValue) {
      this.checked = this.value === this.defValue;
    } else {
      this.checked = this.value;
    }
    console.log(`writeValue: ${this.name} - ${obj}`);
  }

  onChange(event) {
    this.onTouched();
    this.writeValue(event.checked);
    console.log(`onChange: ${this.name} - ${this.value} - ${this.checked}`);
    if (this.defValue) {
      // this.propagateChange(event.checked ? this.value : null);
      this.propagateChange(this.defValue);
    } else {
      this.propagateChange(event.checked);
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
